import { App, Button, Tooltip } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import TruncatedTooltip from '../../components/Common/TruncatedTooltip';
import { Icon } from '../../components/Core/Icon';
import useProtectedFile from '../../hooks/useProtectedFile';
import { copyToClipboard, dateFromTicks } from '../../utils/utils';

export default function ProtectedFile() {
  const { notification } = App.useApp();
  const {
    file,
    isLoading,
    otp,
    handleDownloadFile,
    isDownloadHelpTextVisible,
    isWindowsPlatform,
    formatTime,
    timeLeft,
    getFileOtp,
    isOtpExpired,
  } = useProtectedFile();
  const { t } = useTranslation();

  const canCopyOtp = Boolean(otp && !isOtpExpired);

  return (
    <div className="border-gray-medium h-full w-full overflow-x-hidden rounded-md border bg-white">
      <div className="flex w-full flex-col items-center justify-center gap-4 truncate p-8 text-center">
        <span
          className={clsx(
            'text-dark-blue w-full px-2 text-2xl font-bold transition-all duration-200 md:text-5xl',
            isLoading && 'blur-md',
          )}
        >
          <TruncatedTooltip className="leading-loose">{file?.fileName || 'N/D'}</TruncatedTooltip>
        </span>
        <div className="grid grid-cols-2 items-center gap-2 text-lg">
          <span className="text-blue-dark text-right font-medium">{t('common.author')}:</span>
          <span className="text-left">{file?.createdByName}</span>
          <span className="text-blue-dark text-right font-medium">{t('common.creationDate')}:</span>
          <span className="text-left font-medium">
            {file?.createdAt ? dateFromTicks(file?.createdAt).toLocaleString() : 'N/D'}
          </span>
        </div>
        <Icon name="CameraOffIcon" className="text-blue my-4 h-16 w-16" />
        <div className="border-gray-medium bg-gray-light text-blue-dark flex w-full flex-col items-center gap-2 rounded-md border p-4 md:flex-row">
          <Icon name="CircleAlertIcon" className="text-blue-dark h-10 w-10" />
          <span className="text-wrap">{t('notifications.fileProtectedWithAntiCapture')}</span>
        </div>
        <div className="flex flex-col items-center justify-center gap-1">
          <div className="flex flex-row items-center gap-2">
            <span
              className={clsx(
                'text-blue-dark font-bold tracking-widest transition-all duration-200',
                isLoading && 'blur-md',
                isOtpExpired ? 'text-xl' : 'text-4xl',
              )}
            >
              {otp && !isOtpExpired ? otp : '- - - - - -'}
            </span>
            <Tooltip title={isOtpExpired ? t('common.otpExpired') : t('common.copyToClipboard')}>
              <Button
                type="text"
                size="small"
                disabled={!canCopyOtp}
                loading={isLoading}
                onClick={() => {
                  // Viewer paste format: bare 6-digit code
                  copyToClipboard(String(otp))
                    .then(() => {
                      notification.success({
                        title: t('notifications.otpCopiedToClipboard'),
                      });
                    })
                    .catch(() => {
                      notification.error({
                        title: t('errors.hitASnag'),
                      });
                    });
                }}
              >
                <Icon name="CopyIcon" className="text-gray-dark h-4 w-4" />
              </Button>
            </Tooltip>
          </div>

          <span className={clsx('text-gray-dark text-xs', isLoading && 'blur-md')}>
            {t('common.otpValidFiveMinutesOnce')}
          </span>

          {timeLeft > 0 ? (
            <span className={clsx('text-xs', isLoading && 'blur-md')}>
              {t('common.youCanRequestANewOtpIn')} {formatTime(timeLeft)}
            </span>
          ) : (
            <div
              className={clsx(
                'text-red-dark flex flex-row items-center gap-1 text-xs',
                isLoading && 'blur-md',
              )}
            >
              <Button
                type="link"
                className="text-blue-dark! hover:text-azure! underline"
                loading={isLoading}
                onClick={getFileOtp}
              >
                {t('common.clickHereToGenerateNewOtp')}
              </Button>
            </div>
          )}
        </div>
        <Button
          type="primary"
          loading={isLoading}
          disabled={!otp || !isWindowsPlatform}
          onClick={handleDownloadFile}
        >
          {t('common.openProtectedFile')}
        </Button>
        {isDownloadHelpTextVisible && (
          <span className="text-green-dark text-center font-medium text-wrap">
            {t('notifications.fileProtectedDownloadInProgress')}
          </span>
        )}
        {!isWindowsPlatform && (
          <span className="text-red-dark text-center font-medium text-wrap">
            {t('errors.protectedFileDownloadOnlyForWindows')}
          </span>
        )}
      </div>
    </div>
  );
}
